import {
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Project } from '@/types/project';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { cn } from '@/utils/cn';

interface ProjectCardProps {
  project: Project;
  isSelected?: boolean;
  isDefault?: boolean;
  onSelect?: (project: Project) => void;
  onEdit?: (project: Project) => void;
  onDelete?: (project: Project) => void;
  onManageMembers?: (project: Project) => void;
  onSetDefault?: (project: Project) => void;
}

const tierColors = {
  standard: 'default',
  premium: 'warning',
  enterprise: 'success',
} as const;

const tierLabels = {
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

export function ProjectCard({
  project,
  isSelected = false,
  isDefault = false,
  onSelect,
  onEdit,
  onDelete,
  onManageMembers,
  onSetDefault,
}: ProjectCardProps) {
  const handleCardClick = () => { if (onSelect) onSelect(project); };
  const handleEdit = (e: React.MouseEvent) => { e.stopPropagation(); if (onEdit) onEdit(project); };
  const handleDelete = (e: React.MouseEvent) => { e.stopPropagation(); if (onDelete) onDelete(project); };
  const handleSetDefault = (e: React.MouseEvent) => { e.stopPropagation(); if (onSetDefault) onSetDefault(project); };

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'bg-white rounded-lg border-2 p-6 transition-all duration-200',
        onSelect && 'cursor-pointer hover:shadow-md hover:border-primary-300',
        isSelected ? 'border-primary-500 shadow-md ring-2 ring-primary-100' : 'border-gray-200',
        !project.is_active && 'opacity-60'
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{project.name}</h3>
            {isSelected && <CheckCircleIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />}
            {isDefault && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                <StarIconSolid className="h-3 w-3" />
                Default
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <KeyIcon className="h-4 w-4" />
            <span className="font-mono font-medium">{project.key}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 ml-4">
          {onSetDefault && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSetDefault}
              title={isDefault ? 'This is your default project' : 'Set as default project'}
              className={cn(isDefault ? 'text-amber-500 hover:text-amber-600' : 'hover:bg-amber-50 hover:text-amber-500')}
            >
              {isDefault ? <StarIconSolid className="h-4 w-4" /> : <StarIcon className="h-4 w-4" />}
            </Button>
          )}
          {onManageMembers && (
            <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onManageMembers(project); }} title="Manage members" className="hover:bg-blue-50 hover:text-blue-600">
              <UsersIcon className="h-4 w-4" />
            </Button>
          )}
          {onEdit && (
            <Button variant="ghost" size="sm" onClick={handleEdit} title="Edit project" className="hover:bg-gray-100">
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={handleDelete} title="Delete project" className="hover:bg-red-50 hover:text-danger-600">
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {project.description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {project.customer_name && (
          <div className="flex items-center gap-1.5 text-sm text-gray-700">
            <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
            <span className="truncate">{project.customer_name}</span>
          </div>
        )}
        <Badge variant={tierColors[project.customer_tier]}>{tierLabels[project.customer_tier]}</Badge>
        {!project.is_active && (
          <Badge variant="danger"><XCircleIcon className="h-3 w-3 mr-1" />Inactive</Badge>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
          {project.updated_at !== project.created_at && (
            <span>Updated {new Date(project.updated_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectCard;
